/**
 * Share the page, or fall back to putting its address on the clipboard.
 *
 * `navigator.share` is the iOS sheet the reference's share glyph promises, and it
 * only exists on a secure origin from a user gesture. Everywhere else — desktop
 * Chrome, an insecure dev origin — the clipboard is the honest second best, and
 * the caller tells the user which one happened rather than the button looking
 * like it did nothing.
 *
 * Takes `navigator` as a defaulted parameter so it is testable and SSR-safe.
 */

export const SHARE_RESULTS = ['shared', 'copied', 'cancelled', 'unavailable'];

export async function shareLink(
    { title, text, url },
    nav = typeof navigator === 'undefined' ? null : navigator,
) {
    if (!nav) {
        return 'unavailable';
    }

    if (typeof nav.share === 'function') {
        try {
            await nav.share({ title, text, url });

            return 'shared';
        } catch (error) {
            // Dismissing the sheet is a decision, not a failure — falling through
            // to the clipboard would copy a link the user just declined to share.
            if (error?.name === 'AbortError') {
                return 'cancelled';
            }
        }
    }

    if (typeof nav.clipboard?.writeText === 'function') {
        try {
            await nav.clipboard.writeText(url);

            return 'copied';
        } catch (error) {
            return 'unavailable';
        }
    }

    return 'unavailable';
}
